// Shared by the admin app: which events/collections exist, how to normalize each event's differently-shaped
// documents into one common model, and the day-wise summary + detail-table column model built from it.
import { AgeCounts, DayMeals } from './booking';
import { ANANDAMELA, COLLECTION as DP26_COLLECTION, DAYS as DP26_DAYS, EVENT_NAME as DP26_NAME, estimateFeeCents, toCents } from './durgaPuja2026';
import { MealReservation } from './mealReservation';

export interface AdminDay {
  key: string; // matches a key in NormalizedReservation.meals, unless isAnandamela
  label: string;
  isAnandamela?: boolean;
}

export interface AdminEvent {
  id: string;
  name: string;
  collection: string; // Firestore collection = one document per reservation
  schema: 'v2026' | 'legacy2025'; // which document shape (and so which normalizer) this collection holds
  days: AdminDay[];
}

// Registering a new event here is the one code change needed for the admin app to pick it up (same pattern
// the rest of the codebase already follows for adding an event - see CODEBASE_MAP.md).
export const ADMIN_EVENTS: AdminEvent[] = [
  {
    id: 'durga-puja-2026', name: DP26_NAME, collection: DP26_COLLECTION, schema: 'v2026',
    days: [
      ...DP26_DAYS.map((d) => ({ key: d.date, label: `${d.dateLabel} · ${d.name}` })),
      { key: ANANDAMELA.date, label: `${ANANDAMELA.dateLabel} · ${ANANDAMELA.name}`, isAnandamela: true },
    ],
  },
  {
    id: 'durga-puja-2025', name: 'Durga Puja 2025', collection: 'durga_puja_2025-mealReservations', schema: 'legacy2025',
    days: [
      { key: '2025-09-28', label: 'Sun, 28 Sep 2025 · Shashti' },
      { key: '2025-09-29', label: 'Mon, 29 Sep 2025 · Saptami' },
      { key: '2025-09-30', label: 'Tue, 30 Sep 2025 · Ashtami' },
      { key: '2025-10-01', label: 'Wed, 1 Oct 2025 · Anandamela', isAnandamela: true },
    ],
  },
];

export type ReservationPaymentStatus = 'paid' | 'free' | 'processing' | 'failed' | 'unknown';

/** One event's reservations, once read from Firestore, always end up in this one shape - regardless of which
 * schema they came from - so the summary/detail views never need to know which event they're looking at. */
export interface NormalizedReservation {
  id: string; // Firestore document id
  name: string;
  email: string;
  totalAmount: number;
  donation: number; // EUR; always 0 for legacy2025, which had no donation feature
  paymentStatus: ReservationPaymentStatus;
  createdAtMs: number;
  meals: Record<string, DayMeals>; // keyed by AdminDay.key, absent/zero for days with no reservation
  anandamela: AgeCounts;
  // Cancellation is only ever recorded for the v2026 schema (see AdminEvent.schema) - always undefined for legacy2025.
  cancellation?: { atMs: number; byAdmin: string };
}

const zeroCounts = (): AgeCounts => ({ infant: 0, kid: 0, adult: 0 });
const millisOf = (ts: any): number => (ts && typeof ts.toMillis === 'function' ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : 0);

function normalizeV2026(id: string, raw: any): NormalizedReservation {
  return {
    id,
    name: raw.customerInfo?.name ?? '',
    email: raw.customerInfo?.email ?? '',
    totalAmount: raw.totalAmount ?? 0,
    donation: raw.donation ?? 0,
    paymentStatus: raw.paymentStatus === 'paid' || raw.paymentStatus === 'free' ? raw.paymentStatus : 'unknown',
    createdAtMs: millisOf(raw.createdAt),
    meals: raw.meals ?? {},
    anandamela: { ...zeroCounts(), ...raw.anandamela },
    cancellation: raw.cancellation ? { atMs: millisOf(raw.cancellation.at), byAdmin: raw.cancellation.byAdmin } : undefined,
  };
}

// The 2025 booking page encoded each line item as a composite key like "veg-1-adult" (day number, not a date) in
// `selectedItems`, and Anandamela footfall as "anandamela-<ageGroup>". This mirrors the old admin app's parser,
// which defensively also accepted a JSON-string key or a looser regex match, in case either ever occurred.
function parseLegacyItemId(itemId: string): { mealType: string; day: string; ageGroup: string } | null {
  const parts = itemId.split('-');
  if (parts.length >= 3) return { mealType: parts[0], day: parts[1].replace(/^day/i, ''), ageGroup: parts[2] };
  try {
    const j = JSON.parse(itemId);
    if (j?.mealType && j?.day && j?.ageGroup) return { mealType: j.mealType, day: String(j.day).replace(/^day/i, ''), ageGroup: j.ageGroup };
  } catch {
    // not JSON, fall through to the regex attempt below
  }
  const mealType = itemId.match(/(veg|nonveg)/i)?.[0].toLowerCase();
  const day = (itemId.match(/day(\d+)/i)?.[1] ?? itemId.match(/(\d+)/)?.[1]);
  const ageGroup = itemId.match(/(adult|child|infant)/i)?.[0].toLowerCase();
  return mealType && day && ageGroup ? { mealType, day, ageGroup } : null;
}

const LEGACY_DAY_KEY: Record<string, string> = { '1': '2025-09-28', '2': '2025-09-29', '3': '2025-09-30' };
const LEGACY_AGE: Record<string, keyof AgeCounts> = { adult: 'adult', child: 'kid', infant: 'infant' };
const LEGACY_STATUS: Record<string, ReservationPaymentStatus> = { succeeded: 'paid', processing: 'processing', failed: 'failed' };

function normalizeLegacy2025(id: string, raw: MealReservation): NormalizedReservation {
  const meals: Record<string, DayMeals> = {};
  const anandamela = zeroCounts();
  for (const [itemId, details] of Object.entries(raw.selectedItems ?? {})) {
    const quantity = (details as any)?.quantity ?? 0;
    if (!quantity) continue;
    if (itemId.startsWith('anandamela-')) {
      const age = LEGACY_AGE[itemId.split('-')[1]?.toLowerCase()];
      if (age) anandamela[age] += quantity;
      continue;
    }
    const parsed = parseLegacyItemId(itemId);
    if (!parsed) continue;
    const dayKey = LEGACY_DAY_KEY[parsed.day];
    const age = LEGACY_AGE[parsed.ageGroup];
    const mealType = parsed.mealType === 'nonveg' ? 'nonveg' : parsed.mealType === 'veg' ? 'veg' : null;
    if (!dayKey || !age || !mealType) continue;
    (meals[dayKey] ??= { veg: zeroCounts(), nonveg: zeroCounts() })[mealType][age] += quantity;
  }
  return {
    id,
    name: raw.customerInfo?.name ?? '',
    email: raw.customerInfo?.email ?? '',
    totalAmount: raw.totalAmount ?? 0,
    donation: 0,
    paymentStatus: LEGACY_STATUS[raw.paymentStatus ?? ''] ?? 'unknown',
    createdAtMs: millisOf(raw.createdAt),
    meals,
    anandamela,
  };
}

export const normalizeReservation = (event: AdminEvent, id: string, raw: any): NormalizedReservation =>
  event.schema === 'v2026' ? normalizeV2026(id, raw) : normalizeLegacy2025(id, raw);

/** Aggregated, event-wide counts for the summary cards: per day, veg/nonveg (or just totals for the Anandamela day) by age group. */
export interface DaySummary {
  day: AdminDay;
  veg: AgeCounts;
  nonveg: AgeCounts;
  anandamela: AgeCounts;
}
export interface EventSummary {
  totalReservations: number;
  totalAmount: number;
  totalDonations: number;
  byDay: DaySummary[];
}

const addCounts = (a: AgeCounts, b: AgeCounts) => { a.infant += b.infant; a.kid += b.kid; a.adult += b.adult; };

export function summarizeByDay(rows: NormalizedReservation[], event: AdminEvent): EventSummary {
  const byDay = event.days.map((day): DaySummary => ({ day, veg: zeroCounts(), nonveg: zeroCounts(), anandamela: zeroCounts() }));
  for (const row of rows) {
    for (const summary of byDay) {
      if (summary.day.isAnandamela) addCounts(summary.anandamela, row.anandamela);
      else if (row.meals[summary.day.key]) { addCounts(summary.veg, row.meals[summary.day.key].veg); addCounts(summary.nonveg, row.meals[summary.day.key].nonveg); }
    }
  }
  return {
    totalReservations: rows.length,
    totalAmount: rows.reduce((s, r) => s + r.totalAmount, 0),
    totalDonations: rows.reduce((s, r) => s + r.donation, 0),
    byDay,
  };
}

/** One column of the detail table. `group` is the spanning day header (blank for the fixed columns); the same
 * array drives the on-screen table and the CSV export, so they can never drift apart. */
export interface DetailColumn {
  group: string;
  label: string;
  align: 'left' | 'right';
  get: (row: NormalizedReservation) => string | number;
}

const AGE_LABEL: Record<keyof AgeCounts, string> = { infant: 'Infant', kid: 'Kid', adult: 'Adult' };
const STATUS_LABEL: Record<ReservationPaymentStatus, string> = { paid: 'Paid', free: 'Free', processing: 'Processing', failed: 'Failed', unknown: 'Unknown' };

// Stripe's fee is never stored - it's recomputed here from the same estimate used at checkout time (see
// estimateFeeCents), so "what we actually keep" is always derived fresh rather than trusted from storage.
const netReceivedEur = (r: NormalizedReservation) => r.totalAmount - estimateFeeCents(toCents(r.totalAmount)) / 100;

export function detailColumns(event: AdminEvent): DetailColumn[] {
  const cols: DetailColumn[] = [
    { group: '', label: 'Name', align: 'left', get: (r) => r.name },
    { group: '', label: 'Email', align: 'left', get: (r) => r.email },
    { group: '', label: 'Total (EUR)', align: 'right', get: (r) => r.totalAmount.toFixed(2) },
    { group: '', label: 'Donation (EUR)', align: 'right', get: (r) => r.donation.toFixed(2) },
    { group: '', label: 'Est. net after Stripe fee (EUR)', align: 'right', get: (r) => netReceivedEur(r).toFixed(2) },
    { group: '', label: 'Payment status', align: 'left', get: (r) => STATUS_LABEL[r.paymentStatus] },
    { group: '', label: 'Reserved at', align: 'left', get: (r) => (r.createdAtMs ? new Date(r.createdAtMs).toLocaleString() : '') },
  ];
  if (event.schema === 'v2026') {
    cols.push(
      { group: '', label: 'Cancelled', align: 'left', get: (r) => (r.cancellation ? 'Yes' : 'No') },
      { group: '', label: 'Cancelled at', align: 'left', get: (r) => (r.cancellation ? new Date(r.cancellation.atMs).toLocaleString() : '') },
      { group: '', label: 'Cancelled by', align: 'left', get: (r) => r.cancellation?.byAdmin ?? '' },
    );
  }
  for (const day of event.days) {
    if (day.isAnandamela) {
      (Object.keys(AGE_LABEL) as (keyof AgeCounts)[]).forEach((age) =>
        cols.push({ group: day.label, label: AGE_LABEL[age], align: 'right', get: (r) => r.anandamela[age] || '' })
      );
    } else {
      (['veg', 'nonveg'] as const).forEach((type) =>
        (Object.keys(AGE_LABEL) as (keyof AgeCounts)[]).forEach((age) =>
          cols.push({
            group: day.label,
            label: `${type === 'veg' ? 'Veg' : 'Non-veg'} ${AGE_LABEL[age]}`,
            align: 'right',
            get: (r) => r.meals[day.key]?.[type]?.[age] || '',
          })
        )
      );
    }
  }
  return cols;
}
